/* global axios */
import ApiClient from '../ApiClient';
import { browserTimeParams } from '../../helper/timezoneOffset';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    signal,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
      // Lets callers with an AbortController cancel the request, not just discard
      // its response (mirrors api/search.js).
      signal,
    });
  }

  filter(payload) {
    return axios.post(
      `${this.url}/filter`,
      // `and_payload` is a second condition group the backend ANDs with
      // `payload` as its own parenthesized group. The Kanban board uses it to
      // carry the user's advanced filter while `payload` pins the column, so an
      // `or` inside the user filter cannot escape the column condition.
      { ...payload.queryData, and_payload: payload.andPayload || undefined },
      {
        params: {
          page: payload.page,
          sort_by: payload.sortBy,
          // Kanban-only: skip the assignee COUNT queries the board never reads.
          lean_count: payload.leanCount || undefined,
          count_limit: payload.countLimit || undefined,
          // Kanban paginates 50 at a time; `Conversations::FilterService`
          // honors `per_page` (capped at MAX_PER_PAGE). Absent, the service
          // keeps the default page size the list relies on.
          per_page: payload.pageSize || undefined,
          ...browserTimeParams(),
        },
        signal: payload.signal,
      }
    );
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  // Translates a conversation uuid into the account-local display_id the dashboard routes on.
  // Dedicated endpoint on purpose: `show` only ever takes a display_id.
  lookupByUuid(uuid) {
    return axios.get(`${this.url}/lookup_by_uuid/${uuid}`);
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
      human_agent: true,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`
    );
  }

  assignTeam({ conversationId, teamId, aiHandoffConfirmed }) {
    const params = { team_id: teamId };
    // Only sent after the user accepts the AI-handoff modal; the backend
    // escalates to ClaudIA exclusively when this flag is present.
    if (aiHandoffConfirmed) params.ai_handoff_confirmed = true;
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  toggleEmailContinuity({ conversationId }) {
    return axios.patch(`${this.url}/${conversationId}/force_email_channel`);
  }
}

export default new ConversationApi();
